import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MessageCircle, FileText, Download } from 'lucide-react';
import { companyInfo } from '../mock';

/**
 * Clean digital business card landing page (QR scan destination).
 * Minimal layout: Logo + Phone + Email + 2 CTAs (Request Quotation, WhatsApp).
 */
const SmartCard = () => {
  const navigate = useNavigate();

  const phoneRaw = '6598366186';
  const phoneDisplay = '+65 9836 6186';
  const email = companyInfo.email;
  const waMsg = encodeURIComponent("Hi NJK Logistics, I'd like to enquire about your services.");
  const waUrl = `https://wa.me/${phoneRaw}?text=${waMsg}`;

  // vCard download (Save Contact)
  const downloadVCard = () => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:NJK Logistics`,
      `ORG:${companyInfo.name}`,
      `TITLE:Heavy Vehicle Logistics & Crane Services`,
      `TEL;TYPE=CELL,VOICE:+${phoneRaw}`,
      `EMAIL;TYPE=WORK:${email}`,
      `URL:https://njklogisticsservices.com`,
      `ADR;TYPE=WORK:;;${companyInfo.address};Singapore;;;Singapore`,
      `NOTE:${companyInfo.tagline} | PayNow ${companyInfo.paynow}`,
      'END:VCARD',
    ].join('\n');
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'NJK_Logistics.vcf';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-gray-200 flex items-center justify-center p-5">
      <div className="w-full max-w-md">

        {/* Business Card */}
        <div
          className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100"
          data-testid="business-card"
        >
          {/* Logo header — on white so the logo's built-in design sits clean */}
          <div className="bg-white px-6 py-6 flex items-center justify-center">
            <img
              src={companyInfo.logo}
              alt="NJK Logistics"
              className="h-28 w-auto object-contain"
              data-testid="card-logo"
            />
          </div>

          {/* Contact info — business card style with dark left bars */}
          <div className="px-0 py-6 space-y-3">
            {/* Phone row */}
            <a
              href={`tel:+${phoneRaw}`}
              className="flex items-center group active:scale-[0.99] transition-transform"
              data-testid="contact-phone"
            >
              <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white pl-6 pr-8 py-3 flex items-center gap-3"
                   style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 16px) 100%, 0 100%)' }}>
                <Phone size={20} strokeWidth={2.4} />
              </div>
              <div className="flex-1 px-5">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Phone</div>
                <div className="text-lg font-bold text-gray-900 tracking-tight">{phoneDisplay}</div>
              </div>
            </a>

            {/* Email row */}
            <a
              href={`mailto:${email}`}
              className="flex items-center group active:scale-[0.99] transition-transform"
              data-testid="contact-email"
            >
              <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white pl-6 pr-8 py-3 flex items-center gap-3"
                   style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 16px) 100%, 0 100%)' }}>
                <Mail size={20} strokeWidth={2.4} />
              </div>
              <div className="flex-1 px-5 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Email</div>
                <div className="text-sm font-bold text-gray-900 truncate">{email}</div>
              </div>
            </a>
          </div>

          {/* Website footer band */}
          <div className="bg-gradient-to-r from-[#2a0000] via-[#5a0000] to-[#2a0000] text-white text-center py-3 tracking-[0.2em] font-bold text-sm">
            NJKLOGISTICSSERVICES.COM
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button
            onClick={() => navigate('/booking?type=quote')}
            className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-4 rounded-xl shadow-lg active:scale-95 transition-all flex flex-col items-center justify-center gap-1.5"
            data-testid="btn-quote"
          >
            <FileText size={22} strokeWidth={2.4} />
            <span className="text-sm leading-tight">Request<br/>Quotation</span>
          </button>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-4 px-4 rounded-xl shadow-lg active:scale-95 transition-all flex flex-col items-center justify-center gap-1.5"
            data-testid="btn-whatsapp"
          >
            <MessageCircle size={22} strokeWidth={2.4} />
            <span className="text-sm leading-tight">WhatsApp<br/>Us</span>
          </a>
        </div>

        {/* Save contact link */}
        <button
          onClick={downloadVCard}
          className="w-full mt-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold py-3 px-4 rounded-xl shadow-sm active:scale-95 transition-all flex items-center justify-center gap-2"
          data-testid="btn-save-contact"
        >
          <Download size={18} />
          Save Contact to Phone
        </button>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} {companyInfo.name}
        </p>
      </div>
    </div>
  );
};

export default SmartCard;
